import type { Availability } from "@clinic/shared";
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@clinic/ui";
import { Skeleton, SkeletonStatus } from "@clinic/ui/components/skeleton";
import { cn } from "@clinic/ui/lib/cn";
import { formatTime } from "@web/shared/lib/format";

export interface SlotPickerProps {
  readonly availability: Availability | undefined;
  readonly isLoading: boolean;
  readonly ready: boolean;
  readonly value: string | null;
  readonly onChange: (startsAt: string) => void;
}

export function SlotPicker({
  availability,
  isLoading,
  ready,
  value,
  onChange,
}: SlotPickerProps): JSX.Element {
  const { t } = useTranslation();

  if (!ready) {
    return <Hint icon="info">{t("appointments.slots.chooseDoctorAndDate")}</Hint>;
  }

  if (isLoading) {
    return (
      <div data-testid="slot-picker-loading" className="flex flex-wrap gap-2">
        <SkeletonStatus />
        {[0, 1, 2, 3, 4, 5].map((slot) => (
          <Skeleton key={slot} className="h-(--control-h) w-20 rounded-control" />
        ))}
      </div>
    );
  }

  if (!availability || availability.slots.length === 0) {
    return (
      <Hint icon="calendar">
        {t(`appointments.slots.${availability?.closedReason ?? "none"}`)}
        {availability?.closedNote && (
          <span data-testid="slot-picker-note" className="block text-ink-subtle">
            {availability.closedNote}
          </span>
        )}
      </Hint>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        role="radiogroup"
        data-testid="slot-picker"
        aria-label={t("appointments.slots.label")}
        className="grid max-h-56 grid-cols-3 gap-1.5 overflow-y-auto sm:grid-cols-4"
      >
        {availability.slots.map((slot) => {
          const selected = slot.startsAt === value;

          return (
            <button
              key={slot.startsAt}
              type="button"
              data-testid={`slot-${slot.start}`}
              role="radio"
              aria-checked={selected}
              disabled={!slot.available}
              onClick={() => onChange(slot.startsAt)}
              dir="ltr"
              className={cn(
                "rounded-control border px-2 py-1.5 text-value tabular-nums",
                "transition-colors duration-150",
                slot.available
                  ? "cursor-pointer border-line-strong text-ink hover:border-primary-500 hover:bg-primary-50"
                  : "cursor-not-allowed border-line bg-inset text-ink-subtle line-through",
                selected &&
                  "border-primary-600 bg-primary-600 text-ink-inverse hover:bg-primary-700",
              )}
            >
              {formatTime(slot.startsAt)}
            </button>
          );
        })}
      </div>

      {availability.closedReason === "fully_booked" && (
        <p data-testid="slot-picker-fully-booked" className="text-label text-warning-700">
          {t("appointments.slots.fully_booked")}
        </p>
      )}
    </div>
  );
}

function Hint({ icon, children }: { icon: "info" | "calendar"; children: ReactNode }) {
  return (
    <p
      data-testid="slot-picker-hint"
      className="flex items-center gap-2 rounded-control bg-inset px-3 py-2.5 text-label text-ink-muted"
    >
      <Icon name={icon} className="size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
